import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { IndeterminateCheckbox } from '@/entities/Table';
import { ActionsHeader } from '@/entities/ActionsHeader/ui/ActionsHeader';
import { TextCell } from '@/entities/TextCell';
import { ClubTableItem } from '@/shared/model/types/table';
import { fieldLabels } from '@/entities/User/model/constants';
import { EditableCell } from '@/shared/ui/EditableCell/EditableCell';
import { useLazyGetRegionsQuery } from '@/features/RegionSearch';
import { RegionInlineSearch } from '@/shared/ui/RegionInlineSearch';
import { ActionsCell } from '@/entities/ActionsCell/ui/ActionsCell';
import { dateFormatFromBack } from '@/shared/lib/utils/dateFormat';
import { Button } from '@/shared/ui/Button/Button';
import tableS from '@/entities/Table/ui/Table/Table.module.scss';

import { Toggle } from '@/shared/ui/Toggle/Toggle';
import tableStyle from '../ui/ClubTable.module.scss';
import { getClubDisplayName, isActiveAccount, isDeletedAccount } from '@/shared/lib/utils/accountPresentation';

type HandleSaveFn = (
  id: number,
  field: keyof Pick<
    ClubTableItem,
    | 'name'
    | 'region'
    | 'lastName'
    | 'firstName'
    | 'patronymic'
    | 'contactPhone'
    | 'loginPrefix'
    | 'legalName'
    | 'payerInn'
    | 'birthDate'
    | 'isActive'
  >,
  value: string | null,
  blockReason?: string | null,
) => Promise<void>;

const RegionCell = ({
  id,
  initialValue,
  onSave,
}: {
  id: number;
  initialValue: string | null;
  onSave: (value: string | null) => Promise<void>;
}) => {
  const [value, setValue] = React.useState<string>(initialValue || '');
  const [triggerRegions] = useLazyGetRegionsQuery();

  React.useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const save = React.useCallback(
    async (val: string | null) => {
      const normalized = val && val.length > 0 ? val : '-';
      await onSave(normalized);
    },
    [onSave],
  );

  const validateRegion = React.useCallback(
    async (val: string) => {
      if (val && val !== '-') {
        const res = await triggerRegions(val);
        const first = res?.data?.regions?.[0]?.split('_')[1];
        if (first === val) {
          await save(val);
        }
        return;
      }
      await save('-');
    },
    [triggerRegions, save],
  );

  const onInlineChange = React.useCallback((e: string | React.ChangeEvent<HTMLInputElement>) => {
    const next = typeof e === 'string' ? e : e.target.value;
    setValue(next);
  }, []);

  return (
    <RegionInlineSearch
      value={value}
      onChange={onInlineChange}
      onPick={(v) => {
        void save(v);
      }}
      onClose={() => {
        void validateRegion(value);
      }}
      placeholder="Введите регион"
    />
  );
};

export const getClubTableColumns = (
  handleSave: HandleSaveFn,
  onOpenAccounts: (id: number, name: string, readOnly?: boolean) => void,
  onOpenLog: (id: number, name: string) => void,
  onOpenServers?: (id: number, name: string) => void,
  onOpenTime?: (id: number, name: string, readOnly?: boolean) => void,
  onToggleActive?: (id: number, currentActive: boolean) => void,
): ColumnDef<ClubTableItem>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        isHeader
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        {...{
          checked: row.getIsSelected(),
          disabled: !row.getCanSelect(),
          indeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      thStyle: {
        position: 'sticky',
        left: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--white-100)',
        width: 'var(--sticky-select-width)',
      },
      tdStyle: {
        position: 'sticky',
        left: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--white-100)',
        width: 'var(--sticky-select-width)',
      },
    },
  },
  {
    header: 'Название клуба',
    accessorKey: 'name',
    meta: {
      thStyle: {
        position: 'sticky',
        left: 'calc(var(--sticky-select-width) - var(--sticky-fade-width) + 1px)',
        zIndex: 'var(--z-sticky-fore)',
        backgroundColor: 'var(--white-100)',
        WebkitMaskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        maskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        WebkitMaskClip: 'padding-box',
        maskClip: 'padding-box',
      },
      tdStyle: {
        position: 'sticky',
        left: 'calc(var(--sticky-select-width) - var(--sticky-fade-width) + 1px)',
        zIndex: 'var(--z-sticky-fore)',
        backgroundColor: 'var(--white-100)',
        WebkitMaskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        maskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        paddingRight: 'calc(4px + var(--sticky-fade-width))',
      },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell value={getClubDisplayName(row.original)} />
      ) : (
        <EditableCell
          value={row.getValue('name') as string | null}
          field="name"
          type="string"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'name', v)}
        />
      ),
  },
  {
    header: 'Префикс',
    accessorKey: 'loginPrefix',
    meta: {
      thStyle: { minWidth: '8ch' },
      tdStyle: {
        minWidth: '8ch',
        overflow: 'visible',
      },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell
          value={isDeletedAccount(row.original.accountStatus) ? null : (row.getValue('loginPrefix') as string | null)}
        />
      ) : (
        <EditableCell
          value={row.getValue('loginPrefix') as string | null}
          field="loginPrefix"
          type="string"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'loginPrefix', v)}
        />
      ),
  },
  {
    header: fieldLabels.lastName,
    accessorKey: 'lastName',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell
          value={isDeletedAccount(row.original.accountStatus) ? null : (row.getValue('lastName') as string | null)}
        />
      ) : (
        <EditableCell
          value={row.getValue('lastName') as string | null}
          field="lastName"
          type="string"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'lastName', v)}
        />
      ),
  },
  {
    header: fieldLabels.firstName,
    accessorKey: 'firstName',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell
          value={isDeletedAccount(row.original.accountStatus) ? null : (row.getValue('firstName') as string | null)}
        />
      ) : (
        <EditableCell
          value={row.getValue('firstName') as string | null}
          field="firstName"
          type="string"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'firstName', v)}
        />
      ),
  },
  {
    header: fieldLabels.patronymic,
    accessorKey: 'patronymic',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell
          value={isDeletedAccount(row.original.accountStatus) ? null : (row.getValue('patronymic') as string | null)}
        />
      ) : (
        <EditableCell
          value={row.getValue('patronymic') as string | null}
          field="patronymic"
          type="string"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'patronymic', v)}
        />
      ),
  },
  {
    header: fieldLabels.phoneNumber,
    accessorKey: 'contactPhone',
    meta: {
      tdStyle: {
        maxWidth: '210px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell value={row.getValue('contactPhone') as string | null} />
      ) : (
        <EditableCell
          value={row.getValue('contactPhone') as string | null}
          field="contactPhone"
          type="phone"
          clubType={row.original.clubType}
          onSave={(v) => handleSave(row.original.id, 'contactPhone', v)}
        />
      ),
  },
  {
    header: fieldLabels.birthDate,
    accessorKey: 'birthDate',
    cell: ({ row }) => {
      const rawDate = row.getValue('birthDate') as string | null;
      const formattedDate = rawDate ? dateFormatFromBack(rawDate) : null;
      const isIndividual = row.original.clubType === 'individual';
      if (!isActiveAccount(row.original.accountStatus)) {
        return <TextCell value={formattedDate} />;
      }
      return (
        <EditableCell
          value={formattedDate}
          field="birthDate"
          type="date"
          clubType={row.original.clubType}
          disabled={!isIndividual}
          onSave={(v) => handleSave(row.original.id, 'birthDate', v)}
        />
      );
    },
  },
  {
    header: fieldLabels.region,
    accessorKey: 'region',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell value={row.getValue('region') as string | null} />
      ) : (
        <RegionCell
          id={row.original.id}
          initialValue={(row.getValue('region') as string | null) ?? ''}
          onSave={(val) => handleSave(row.original.id, 'region', val)}
        />
      ),
  },
  {
    header: 'Юр. лицо',
    accessorKey: 'legalName',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) => {
      const isLegal = row.original.clubType === 'legal';
      if (!isActiveAccount(row.original.accountStatus)) {
        return <TextCell value={row.getValue('legalName') as string | null} />;
      }
      return (
        <EditableCell
          value={row.getValue('legalName') as string | null}
          field="legalName"
          type="string"
          clubType={row.original.clubType}
          disabled={!isLegal}
          onSave={(v) => handleSave(row.original.id, 'legalName', v)}
        />
      );
    },
  },
  {
    header: 'ИНН',
    accessorKey: 'payerInn',
    meta: {
      thStyle: { width: '17ch', minWidth: '15ch', maxWidth: '19ch' },
      tdStyle: { width: '17ch', minWidth: '15ch', maxWidth: '19ch' },
    },
    cell: ({ row }) => {
      const isLegal = row.original.clubType === 'legal';
      if (!isActiveAccount(row.original.accountStatus)) {
        return <TextCell value={row.getValue('payerInn') as string | null} />;
      }
      return (
        <EditableCell
          value={row.getValue('payerInn') as string | null}
          field="payerInn"
          type="string"
          clubType={row.original.clubType}
          disabled={!isLegal}
          onSave={(v) => handleSave(row.original.id, 'payerInn', v)}
        />
      );
    },
  },
  {
    header: 'Админ / Игрок',
    accessorKey: 'adminsPlayers',
    enableSorting: false,
    meta: {
      thClassName: tableS.thPadding14,
    },
    cell: ({ row }) => (
      <TextCell
        value={
          row.original.adminsCount === null || row.original.playersCount === null
            ? null
            : `${row.original.adminsCount} / ${row.original.playersCount}`
        }
      />
    ),
  },

  {
    header: 'Баланс',
    accessorKey: 'balanceTechnocoins',
    meta: {
      thStyle: { width: '160px', minWidth: '150px' },
      tdStyle: { width: '160px', minWidth: '150px' },
      tdClassName: tableS.cellCenter,
    },
    cell: ({ row }) => {
      const balance = (row.original.balanceTechnocoins as number | null | undefined) ?? 0;
      const balanceLimit = (row.original.balanceLimit as number | null | undefined) ?? 0;
      const unlimitedUntil = row.original.unlimitedUntil ?? null;
      const isUnlimitedActive = (() => {
        if (!unlimitedUntil) return false;
        const until = new Date(unlimitedUntil);
        if (Number.isNaN(until.getTime())) return false;
        return until.getTime() > Date.now();
      })();

      if (isUnlimitedActive) {
        const until = new Date(unlimitedUntil as string);
        const label = until.toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        return (
          <Button
            width="auto"
            size="xs"
            variant="fill"
            onClick={(e) => {
              e.stopPropagation();
              onOpenTime?.(
                row.original.id,
                getClubDisplayName(row.original),
                !isActiveAccount(row.original.accountStatus),
              );
            }}
          >
            {label}
          </Button>
        );
      }

      return (
        <Button
          width="auto"
          size="xs"
          variant="fill"
          onClick={(e) => {
            e.stopPropagation();
            onOpenTime?.(
              row.original.id,
              getClubDisplayName(row.original),
              !isActiveAccount(row.original.accountStatus),
            );
          }}
        >
          {balance.toLocaleString('ru-RU')} Ŧ{balanceLimit > 0 && balance <= balanceLimit ? ' ⚠' : ''}
        </Button>
      );
    },
  },
  {
    header: fieldLabels.isActive,
    accessorKey: 'isActive',
    cell: ({ row }) =>
      !isActiveAccount(row.original.accountStatus) ? (
        <TextCell value={null} />
      ) : (
        <div className={tableStyle.toggleCell}>
          <Toggle
            isActive={row.original.isActive}
            onToggle={() => onToggleActive?.(row.original.id, row.original.isActive)}
          />
        </div>
      ),
  },
  {
    header: () => <ActionsHeader />,
    accessorKey: 'actions',
    enableSorting: false,
    cell: (row) => (
      <ActionsCell
        row={row}
        onOpenLog={onOpenLog}
        showHistory={true}
        showServers={true}
        onOpenServers={onOpenServers}
        onOpenAccounts={onOpenAccounts}
        deleteMode="club"
      />
    ),
  },
];
